import { Stack, Tooltip } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { ButtonWithDropdown, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { WorkInstructionUIStore } from '../../stores/work-instruction.ui-store'

interface Props {
  store: WorkInstructionUIStore
}

// Renders the top-right action menu (Amend / Cancel group) above the internal-moves
// WI table when a real housekeeping plan group is selected. Nothing renders for the
// Others bucket or when no group is selected.
//
// Disabled state: per AC, both Amend and Cancel are disabled when zero WIs in the
// selected group are in Todo status — interpreted here as "no pending moves left to
// amend or cancel" (every move is either picked up or finished).
export const HousekeepingGroupActions = observer(({ store }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  if (!store.isHousekeepingPlanSelected) {
    return null
  }

  const disabled = store.pendingHousekeepingMovesCount === 0
  const tooltipText = disabled
    ? t('internalMoves.allInProgressTooltip', 'All moves are in progress.')
    : ''

  return (
    <Stack
      direction='row'
      justifyContent='flex-end'
      padding={theme.customSpacing.s}
      data-cy='housekeeping-group-actions'
    >
      <Tooltip title={tooltipText} arrow disableHoverListener={!disabled}>
        {/* span lets the tooltip anchor even when the inner trigger has disabled options. */}
        <span>
          <ButtonWithDropdown
            label={t('internalMoves.groupActions', 'Group actions')}
            size='small'
            color='inherit'
            variant='outlined'
            testId='housekeeping-group-actions-menu'
            options={[
              {
                label: t('internalMoves.amend', 'Amend'),
                disabled,
                onClick: () => store.openAmendHousekeepingPlanDialog(),
                testId: 'housekeeping-group-amend',
              },
              {
                label: t('internalMoves.cancelGroup', 'Cancel group'),
                disabled,
                onClick: () => store.openCancelHousekeepingPlanDialog(),
                testId: 'housekeeping-group-cancel',
              },
            ]}
          />
        </span>
      </Tooltip>
    </Stack>
  )
})
